import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AdminPricesApiError,
  adminAddModelPrice,
  adminCloseModelPrice,
  adminListModelPrices,
  adminListTenants,
  adminModelPriceHistory,
  adminRecalcApply,
  adminRecalcPreview,
} from "../api/client";
import type {
  AdminTenant,
  ModelPriceSummary,
  PriceInput,
  PriceView,
  RecalcApplyView,
  RecalcReportView,
  RecalcRequest,
} from "../api/types";
import { ConfirmModal } from "../components/ConfirmModal";

/**
 * Pagina admin "Listino prezzi" (run `prezzi-listino`, criterio A6).
 *
 * Il listino e' una storia, non un valore: ogni modello ha un prezzo
 * corrente (dal YAML del repo o da una riga in tabella), uno storico di
 * prezzi con validita' temporale, e la possibilita' di ricalcolare i costi
 * gia' registrati in `llm_calls` con anteprima -> conferma -> esito.
 * Un dato che il backend non conosce si mostra come "—", mai come 0: nel
 * listino uno zero significa "gratis" e va dichiarato esplicitamente.
 */

const NO_DATA = "—";
const TZ = "Europe/Rome";

const UNIT_LABELS: Record<string, string> = {
  per_token: "per milione di token",
  per_minute: "al minuto",
  per_1k_pages: "per 1000 pagine",
};

/** Motivi tecnici di `not_recomputable` (contratto C2) -> etichetta. */
const REASON_LABELS: Record<string, string> = {
  quantita_assente: "Quantita' assente (token, durata o pagine non registrati)",
  modello_senza_prezzo: "Modello senza prezzo alla data della chiamata",
  deployment_ignoto: "Deployment non riconosciuto",
  modello_ignoto: "Modello non a catalogo",
  unita_incoerente: "Unita' del prezzo incoerente con la chiamata",
};

function unitLabel(unit: string) {
  return UNIT_LABELS[unit] || unit;
}

function reasonLabel(reason: string) {
  return REASON_LABELS[reason] || reason;
}

// Locale italiano: virgola per i decimali, non il punto. `maximumFractionDigits`
// senza un minimo pari fa cadere da solo gli zeri finali (46.0000 -> "46",
// non "46,0000"): un prezzo a 4 decimali si legge quasi sempre con meno cifre.
const _fmtPriceIt = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 4 });
const _fmtDeltaIt = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 2 });

/** Prezzo unitario: fino a 4 decimali, senza zeri finali. `null`/`undefined`/NaN = ignoto. */
function fmtPrice(v: number | null | undefined) {
  return typeof v === "number" && Number.isFinite(v) ? `€${_fmtPriceIt.format(v)}` : NO_DATA;
}

/** Delta di ricalcolo: fino a 2 decimali, senza zeri finali, con segno esplicito. */
function fmtDelta(v: number | null | undefined) {
  if (typeof v !== "number" || !Number.isFinite(v)) return NO_DATA;
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  return `${sign}€${_fmtDeltaIt.format(Math.abs(v))}`;
}

function fmtInt(v: number | null | undefined) {
  return typeof v === "number" && Number.isFinite(v) ? v.toLocaleString("it-IT") : NO_DATA;
}

/** Istante ISO (con offset) -> data e ora nel fuso italiano. */
function fmtDateTime(iso: string | null | undefined) {
  if (!iso) return NO_DATA;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("it-IT", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: TZ,
  });
}

/** Parti di un istante nel fuso italiano, per precompilare gli input. */
function romeParts(d: Date) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return { ymd: `${get("year")}-${get("month")}-${get("day")}`, hm: `${hour}:${get("minute")}` };
}

/** Offset di Roma per l'istante UTC dato (usato solo internamente da `romeOffset`). */
function offsetAtInstant(instant: Date): string {
  try {
    const name = new Intl.DateTimeFormat("en-GB", { timeZone: TZ, timeZoneName: "longOffset" })
      .formatToParts(instant).find((p) => p.type === "timeZoneName")?.value ?? "";
    const m = /GMT([+-]\d{2}:\d{2})/.exec(name);
    if (m) return m[1];
    if (name === "GMT") return "+00:00";
  } catch { /* browser senza longOffset: si ricade sotto */ }
  const local = new Date(instant.toLocaleString("en-US", { timeZone: TZ }));
  const minutes = Math.round((local.getTime() - instant.getTime()) / 60000);
  const sign = minutes >= 0 ? "+" : "-";
  const abs = Math.abs(minutes);
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}

/**
 * Offset del fuso italiano ("+02:00") per l'istante che contiene la data
 * locale indicata (ymd/hm letti come ora italiana, non UTC).
 *
 * Prima approssimazione: si legge ymd/hm come se fosse UTC e si guarda che
 * offset ha Roma in quell'istante — vicino al cambio d'ora (la finestra
 * locale 01:00-02:00, ora "saltata" a marzo o "ripetuta" a ottobre) questo
 * puo' cadere dal lato sbagliato della transizione. Si ricalcola percio'
 * l'istante UTC reale con l'offset trovato e si rilegge l'offset di Roma in
 * QUELL'istante: la seconda lettura converge sempre alla risposta corretta.
 */
function romeOffset(ymd: string, hm = "00:00"): string {
  const guess = new Date(`${ymd}T${hm}:00Z`);
  if (Number.isNaN(guess.getTime())) return "+01:00";
  const offset1 = offsetAtInstant(guess);
  const real = new Date(`${ymd}T${hm}:00${offset1}`);
  if (Number.isNaN(real.getTime())) return offset1;
  return offsetAtInstant(real);
}

/** "YYYY-MM-DD" (input date, letto come giorno italiano) -> ISO con offset a mezzanotte. */
function dayToIso(ymd: string): string {
  return `${ymd}T00:00:00${romeOffset(ymd)}`;
}

/** "YYYY-MM-DDTHH:mm" (input datetime-local, letto come ora italiana) -> ISO con offset. */
function localToIso(v: string): string {
  const [ymd, hm] = v.split("T");
  return `${ymd}T${hm || "00:00"}:00${romeOffset(ymd, hm || "00:00")}`;
}

/** ISO -> "YYYY-MM-DDTHH:mm" nel fuso italiano, per `datetime-local`. */
function isoToLocalInput(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : new Date();
  const p = romeParts(Number.isNaN(d.getTime()) ? new Date() : d);
  return `${p.ymd}T${p.hm}`;
}

function todayYmd() {
  return romeParts(new Date()).ymd;
}

/**
 * Guardia per il click sull'overlay di un modale (stesso pattern di
 * `ConfirmModal`): chiude solo se mousedown e click sono partiti entrambi
 * dal backdrop (non da un drag di selezione testo iniziato nel contenuto),
 * e mai mentre `disabled` e' vero (es. un salvataggio in corso, per non
 * perdere lo stato a meta' di un'operazione).
 */
function useOverlayGuard(disabled: boolean, onClose: () => void) {
  const startedOnBackdrop = useRef(false);
  return {
    onMouseDown: (e: React.MouseEvent) => { startedOnBackdrop.current = e.target === e.currentTarget; },
    onClick: (e: React.MouseEvent) => {
      if (!disabled && e.target === e.currentTarget && startedOnBackdrop.current) onClose();
    },
  };
}

function errorMessage(err: unknown): string {
  if (err instanceof AdminPricesApiError) return err.detail;
  return err instanceof Error ? err.message : String(err);
}

/** Sommario del prezzo corrente, coerente con l'unita' del modello. */
/** Riga opzionale coi moltiplicatori (batch, deployment), se il prezzo li porta. */
function MultiplierLine({ price }: { price: PriceView }) {
  const parts: string[] = [];
  if (price.batch_multiplier != null) parts.push(`batch ×${price.batch_multiplier}`);
  if (price.deployment_multipliers && Object.keys(price.deployment_multipliers).length > 0) {
    parts.push(
      Object.entries(price.deployment_multipliers).map(([name, mult]) => `${name} ×${mult}`).join(", "),
    );
  }
  if (parts.length === 0) return null;
  return <span className="text-muted text-xs">{parts.join(" · ")}</span>;
}

function PriceCells({ price, unit }: { price: PriceView | null; unit: string }) {
  if (!price) return <span className="text-muted">{NO_DATA}</span>;
  if (unit === "per_token") {
    const p = price.per_million_tokens;
    return (
      <div className="flex flex-col text-sm">
        <span>input {fmtPrice(p?.input)} · output {fmtPrice(p?.output)}</span>
        {(p?.cache_read != null || p?.cache_write != null) && (
          <span className="text-muted text-xs">
            cache read {fmtPrice(p?.cache_read)} · cache write {fmtPrice(p?.cache_write)}
          </span>
        )}
        <MultiplierLine price={price} />
      </div>
    );
  }
  if (unit === "per_minute") {
    return (
      <div className="flex flex-col text-sm">
        <span>{fmtPrice(price.usd_per_minute)}</span>
        <MultiplierLine price={price} />
      </div>
    );
  }
  if (unit === "per_1k_pages") {
    return (
      <div className="flex flex-col text-sm">
        <span>{fmtPrice(price.usd_per_1k_pages)}</span>
        <MultiplierLine price={price} />
      </div>
    );
  }
  return <span className="text-muted">{NO_DATA}</span>;
}

function SourceBadge({ source }: { source: string }) {
  if (source === "db") return <span className="badge badge-info" title="Prezzo impostato dall'admin, in tabella">Impostato</span>;
  if (source === "yaml") return <span className="badge badge-neutral" title="Prezzo di base dal listino nel repo (catalog.yaml)">Listino</span>;
  return <span className="badge badge-neutral">{source || NO_DATA}</span>;
}

type Dialog =
  | { kind: "new"; model: ModelPriceSummary }
  | { kind: "recalc"; model: ModelPriceSummary }
  | { kind: "close"; model: ModelPriceSummary; price: PriceView }
  | null;

export function AdminPrices() {
  const [models, setModels] = useState<ModelPriceSummary[]>([]);
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [expanded, setExpanded] = useState<Record<string, PriceView[] | "loading" | { error: string }>>({});
  const [dialog, setDialog] = useState<Dialog>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setModels(await adminListModelPrices());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    adminListTenants().then(setTenants).catch(() => { /* il nome del cliente e' solo cosmetico */ });
  }, []);

  const loadHistory = useCallback(async (modelId: string) => {
    setExpanded((prev) => ({ ...prev, [modelId]: "loading" }));
    try {
      const rows = await adminModelPriceHistory(modelId);
      setExpanded((prev) => ({ ...prev, [modelId]: rows }));
    } catch (err) {
      setExpanded((prev) => ({ ...prev, [modelId]: { error: errorMessage(err) } }));
    }
  }, []);

  const toggleHistory = useCallback((modelId: string) => {
    if (expanded[modelId] !== undefined) {
      setExpanded((prev) => { const next = { ...prev }; delete next[modelId]; return next; });
      return;
    }
    loadHistory(modelId);
  }, [expanded, loadHistory]);

  /** Dopo una scrittura: ricarica la lista e, se aperto, lo storico del modello. */
  const afterWrite = useCallback(async (modelId: string, message: string) => {
    setNotice(message);
    await load();
    if (expanded[modelId] !== undefined) await loadHistory(modelId);
  }, [expanded, load, loadHistory]);

  const tenantName = useCallback(
    (id: string | null) => (id == null ? "Senza cliente" : tenants.find((t) => t.tenant_id === id)?.name || id),
    [tenants],
  );

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title gap-2">
          <span className="material-symbols-outlined icon-xl">sell</span>
          Listino prezzi
        </h1>
        <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading} title="Ricarica il listino">
          <span className="material-symbols-outlined">refresh</span>
          Aggiorna
        </button>
      </div>

      <p className="text-sm text-muted mb-4">
        Prezzi dei modelli con validita' temporale. Il listino del repo e' la base; un prezzo impostato
        qui vince nel suo intervallo. I prezzi non si modificano: se ne aggiunge uno nuovo da una data,
        anche passata, e poi si ricalcolano i costi gia' registrati con anteprima e conferma.
      </p>

      {error && (
        <div className="alert alert-danger mb-4" role="alert">
          <span className="material-symbols-outlined">error</span>
          {error}
        </div>
      )}
      {notice && (
        <div className="alert alert-success mb-4" role="status">
          <span className="material-symbols-outlined">check_circle</span>
          <span className="flex-1">{notice}</span>
          <button className="btn btn-ghost btn-xs" onClick={() => setNotice("")} aria-label="Chiudi avviso">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading-center"><span className="spinner spinner-lg" /></div>
      ) : models.length === 0 ? (
        <div className="card text-center text-muted">Nessun modello a catalogo.</div>
      ) : (
        <div className="card">
          <div className="table-scroll">
            <table className="table" data-testid="prices-table">
              <thead>
                <tr>
                  <th>Modello</th>
                  <th>Unita'</th>
                  <th>Prezzo corrente</th>
                  <th>Valido da</th>
                  <th>Provenienza</th>
                  <th>Storico</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {models.map((m) => {
                  const hist = expanded[m.model_id];
                  const open = hist !== undefined;
                  return (
                    <ModelRows
                      key={m.model_id}
                      model={m}
                      history={hist}
                      open={open}
                      onToggleHistory={() => toggleHistory(m.model_id)}
                      onNewPrice={() => setDialog({ kind: "new", model: m })}
                      onRecalc={() => setDialog({ kind: "recalc", model: m })}
                      onClosePrice={(price) => setDialog({ kind: "close", model: m, price })}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {dialog?.kind === "new" && (
        <NewPriceDialog
          model={dialog.model}
          onClose={() => setDialog(null)}
          onSaved={async (created) => {
            setDialog(null);
            await afterWrite(dialog.model.model_id, `Nuovo prezzo salvato per ${dialog.model.model_id} (${created.ref}).`);
          }}
        />
      )}
      {dialog?.kind === "close" && (
        <ClosePriceDialog
          model={dialog.model}
          price={dialog.price}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await afterWrite(dialog.model.model_id, `Prezzo ${dialog.price.ref} chiuso.`);
          }}
        />
      )}
      {dialog?.kind === "recalc" && (
        <RecalcDialog
          model={dialog.model}
          tenants={tenants}
          tenantName={tenantName}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

/* ── Righe della tabella modelli (riga principale + storico espanso) ── */

function ModelRows({
  model, history, open, onToggleHistory, onNewPrice, onRecalc, onClosePrice,
}: {
  model: ModelPriceSummary;
  history: PriceView[] | "loading" | { error: string } | undefined;
  open: boolean;
  onToggleHistory: () => void;
  onNewPrice: () => void;
  onRecalc: () => void;
  onClosePrice: (price: PriceView) => void;
}) {
  return (
    <>
      <tr data-testid={`model-row-${model.model_id}`}>
        <td className="truncate" style={{ maxWidth: 260 }} title={model.model_id}><code>{model.model_id}</code></td>
        <td className="text-sm" style={{ whiteSpace: "nowrap" }}>{unitLabel(model.unit)}</td>
        <td><PriceCells price={model.current} unit={model.unit} /></td>
        <td className="text-sm">{fmtDateTime(model.current?.valid_from)}</td>
        <td><SourceBadge source={model.source} /></td>
        <td>{fmtInt(model.history_count)}</td>
        <td>
          <div className="flex items-center gap-2">
            <button className={`btn btn-xs ${open ? "btn-primary" : "btn-outline"}`} onClick={onToggleHistory} title="Mostra o nascondi lo storico dei prezzi">
              Storico
            </button>
            <button className="btn btn-xs btn-outline" onClick={onNewPrice} title="Aggiungi un prezzo valido da una data">
              Nuovo prezzo
            </button>
            <button className="btn btn-xs btn-outline" onClick={onRecalc} title="Ricalcola i costi registrati con il listino corrente" disabled={!model.current}>
              Ricalcola
            </button>
          </div>
        </td>
      </tr>
      {open && (
        <tr data-testid={`history-${model.model_id}`}>
          <td colSpan={7}>
            {history === "loading" ? (
              <span className="spinner" />
            ) : history && "error" in history ? (
              <div className="alert alert-danger">
                <span className="material-symbols-outlined">error</span>
                {history.error}
              </div>
            ) : Array.isArray(history) && history.length === 0 ? (
              <span className="text-sm text-muted">
                Nessun prezzo impostato in tabella: vale il listino del repo.
              </span>
            ) : Array.isArray(history) ? (
              <HistoryTable rows={history} unit={model.unit} onClosePrice={onClosePrice} />
            ) : null}
          </td>
        </tr>
      )}
    </>
  );
}

function HistoryTable({ rows, unit, onClosePrice }: { rows: PriceView[]; unit: string; onClosePrice: (p: PriceView) => void }) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Rif.</th>
          <th>Prezzo</th>
          <th>Valido da</th>
          <th>Valido fino a</th>
          <th>Fonte</th>
          <th>Nota</th>
          <th>Creato</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p) => (
          <tr key={p.ref}>
            <td><code className="truncate">{p.ref}</code></td>
            <td><PriceCells price={p} unit={unit} /></td>
            <td className="text-sm">{fmtDateTime(p.valid_from)}</td>
            <td className="text-sm">{p.valid_to ? fmtDateTime(p.valid_to) : <span className="badge badge-success badge-sm">aperto</span>}</td>
            <td className="text-sm">
              {p.source_url ? <a href={p.source_url} target="_blank" rel="noreferrer">fonte</a> : <span className="text-muted">{NO_DATA}</span>}
            </td>
            <td className="text-sm">{p.note || <span className="text-muted">{NO_DATA}</span>}</td>
            <td className="text-sm text-muted">
              {fmtDateTime(p.created_at)}{p.created_by ? ` · ${p.created_by}` : ""}
            </td>
            <td>
              {p.id != null && p.valid_to == null && (
                <button className="btn btn-xs btn-ghost" onClick={() => onClosePrice(p)} title="Imposta la fine di validita' di questo prezzo">
                  Chiudi
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ── Modale "Nuovo prezzo" ── */

function parseNum(v: string): number | null {
  if (v.trim() === "") return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
}

function NewPriceDialog({ model, onClose, onSaved }: {
  model: ModelPriceSummary;
  onClose: () => void;
  onSaved: (created: PriceView) => void | Promise<void>;
}) {
  const unit = model.unit;
  // Non "oggi = adesso": un valore fisso a mezzanotte italiana e' sicuro
  // anche per un'anteprima di ricalcolo lanciata subito dopo (niente
  // intervallo vuoto, niente valid_from nel futuro per clock skew).
  const [validFrom, setValidFrom] = useState(todayYmd());
  const [validTo, setValidTo] = useState("");
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [cacheRead, setCacheRead] = useState("");
  const [cacheWrite, setCacheWrite] = useState("");
  const [perMinute, setPerMinute] = useState("");
  const [perPages, setPerPages] = useState("");
  const [batchMultiplier, setBatchMultiplier] = useState("");
  const [deploymentMultipliers, setDeploymentMultipliers] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [note, setNote] = useState("");
  const [allowZero, setAllowZero] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const overlay = useOverlayGuard(saving, onClose);

  function buildBody(): PriceInput | string {
    const body: PriceInput = {
      valid_from: dayToIso(validFrom),
      valid_to: validTo ? dayToIso(validTo) : null,
      source_url: sourceUrl.trim() || null,
      note: note.trim() || null,
      allow_zero: allowZero,
    };
    if (unit === "per_token") {
      const i = parseNum(input); const o = parseNum(output);
      const cr = parseNum(cacheRead); const cw = parseNum(cacheWrite);
      if ([i, o, cr, cw].some((n) => n != null && Number.isNaN(n))) return "I prezzi devono essere numeri (usa il punto per i decimali)";
      if (i == null || o == null) return "Per un modello a token servono i prezzi input e output";
      body.per_million_tokens = { input: i, output: o };
      if (cr != null) body.per_million_tokens.cache_read = cr;
      if (cw != null) body.per_million_tokens.cache_write = cw;
    } else if (unit === "per_minute") {
      const n = parseNum(perMinute);
      if (n == null || Number.isNaN(n)) return "Serve il prezzo al minuto";
      body.usd_per_minute = n;
    } else if (unit === "per_1k_pages") {
      const n = parseNum(perPages);
      if (n == null || Number.isNaN(n)) return "Serve il prezzo per 1000 pagine";
      body.usd_per_1k_pages = n;
    } else {
      return `Unita' non gestita: ${unit}`;
    }
    const bm = parseNum(batchMultiplier);
    if (bm != null && Number.isNaN(bm)) return "Il moltiplicatore batch deve essere un numero";
    if (bm != null) body.batch_multiplier = bm;
    if (deploymentMultipliers.trim() !== "") {
      let parsed: unknown;
      try {
        parsed = JSON.parse(deploymentMultipliers);
      } catch {
        return "I moltiplicatori per deployment non sono JSON valido";
      }
      const ok = parsed != null && typeof parsed === "object" && !Array.isArray(parsed)
        && Object.values(parsed as Record<string, unknown>).every((v) => typeof v === "number" && Number.isFinite(v));
      if (!ok) return 'I moltiplicatori per deployment vanno come oggetto JSON, es. {"eu-west": 1.1}';
      body.deployment_multipliers = parsed as Record<string, number>;
    }
    if (!validFrom) return "Serve la data di inizio validita'";
    if (validTo && validTo <= validFrom) return "La fine di validita' deve essere dopo l'inizio";
    return body;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    const body = buildBody();
    if (typeof body === "string") { setError(body); return; }
    setSaving(true);
    try {
      const created = await adminAddModelPrice(model.model_id, body);
      await onSaved(created);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="dialog-overlay" {...overlay}>
      <div className="dialog-card" role="dialog" aria-modal="true" aria-labelledby="new-price-title" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2 id="new-price-title">Nuovo prezzo — {model.model_id}</h2>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving} aria-label="Chiudi">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <p className="text-sm text-muted mb-4">
          Unita': <strong>{unitLabel(unit)}</strong>. Il prezzo aperto precedente, se c'e', viene chiuso alla data di inizio di questo.
        </p>
        <form onSubmit={handleSubmit} data-testid="new-price-form">
          {unit === "per_token" && (
            <>
              <div className="form-group">
                <label className="form-label" htmlFor="np-input">Input (€ per milione di token)</label>
                <input id="np-input" className="form-input" type="number" step="any" min="0" value={input} onChange={(e) => setInput(e.target.value)} required autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="np-output">Output (€ per milione di token)</label>
                <input id="np-output" className="form-input" type="number" step="any" min="0" value={output} onChange={(e) => setOutput(e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="np-cache-read">Cache read (€ per milione, opzionale)</label>
                <input id="np-cache-read" className="form-input" type="number" step="any" min="0" value={cacheRead} onChange={(e) => setCacheRead(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="np-cache-write">Cache write (€ per milione, opzionale)</label>
                <input id="np-cache-write" className="form-input" type="number" step="any" min="0" value={cacheWrite} onChange={(e) => setCacheWrite(e.target.value)} />
              </div>
            </>
          )}
          {unit === "per_minute" && (
            <div className="form-group">
              <label className="form-label" htmlFor="np-minute">Prezzo al minuto (€)</label>
              <input id="np-minute" className="form-input" type="number" step="any" min="0" value={perMinute} onChange={(e) => setPerMinute(e.target.value)} required autoFocus />
            </div>
          )}
          {unit === "per_1k_pages" && (
            <div className="form-group">
              <label className="form-label" htmlFor="np-pages">Prezzo per 1000 pagine (€)</label>
              <input id="np-pages" className="form-input" type="number" step="any" min="0" value={perPages} onChange={(e) => setPerPages(e.target.value)} required autoFocus />
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="np-batch">Moltiplicatore batch (opzionale)</label>
            <input id="np-batch" className="form-input" type="number" step="any" min="0" value={batchMultiplier} onChange={(e) => setBatchMultiplier(e.target.value)} placeholder="es. 0.5" />
            <small className="form-hint">Fattore applicato alle chiamate in modalita' batch, se il fornitore le scala.</small>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="np-deployment">Moltiplicatori per deployment (opzionale, JSON)</label>
            <textarea id="np-deployment" className="form-textarea" rows={2} value={deploymentMultipliers} onChange={(e) => setDeploymentMultipliers(e.target.value)} placeholder='{"eu-west": 1.1}' />
            <small className="form-hint">Oggetto nome-deployment → moltiplicatore.</small>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="np-valid-from">Valido da</label>
            <input id="np-valid-from" className="form-input" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} required />
            <small className="form-hint">Giorno italiano, dalla mezzanotte. Puo' essere nel passato: poi usa "Ricalcola".</small>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="np-valid-to">Valido fino a (opzionale)</label>
            <input id="np-valid-to" className="form-input" type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
            <small className="form-hint">Vuoto = prezzo aperto, vale finche' non ne arriva uno nuovo.</small>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="np-source">Fonte (URL)</label>
            <input id="np-source" className="form-input" type="url" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://…/pricing" />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="np-note">Nota</label>
            <textarea id="np-note" className="form-textarea" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label flex items-center gap-2">
              <input type="checkbox" checked={allowZero} onChange={(e) => setAllowZero(e.target.checked)} data-testid="np-allow-zero" />
              Questo modello e' gratuito (prezzo zero)
            </label>
            <small className="form-hint">Senza questa spunta un prezzo a zero viene rifiutato: zero vuol dire gratis, non ignoto.</small>
          </div>

          {error && (
            <div className="alert alert-danger mb-4" role="alert" data-testid="new-price-error">
              <span className="material-symbols-outlined">error</span>
              {error}
            </div>
          )}

          <div className="dialog-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>Annulla</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Salvataggio…" : "Salva prezzo"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Modale "Chiudi prezzo" (imposta valid_to) ── */

function ClosePriceDialog({ model, price, onClose, onSaved }: {
  model: ModelPriceSummary;
  price: PriceView;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [validTo, setValidTo] = useState(isoToLocalInput(null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const overlay = useOverlayGuard(saving, onClose);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (price.id == null) return;
    setError("");
    setSaving(true);
    try {
      await adminCloseModelPrice(model.model_id, price.id, localToIso(validTo));
      await onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="dialog-overlay" {...overlay}>
      <div className="dialog-card" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Chiudi prezzo {price.ref}</h2>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving} aria-label="Chiudi">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="cp-valid-to">Valido fino a</label>
            <input id="cp-valid-to" className="form-input" type="datetime-local" value={validTo} onChange={(e) => setValidTo(e.target.value)} required />
            <small className="form-hint">Ora italiana. Da questo istante il modello torna al prezzo precedente o al listino del repo.</small>
          </div>
          {error && (
            <div className="alert alert-danger mb-4" role="alert">
              <span className="material-symbols-outlined">error</span>
              {error}
            </div>
          )}
          <div className="dialog-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>Annulla</button>
            <button type="submit" className="btn btn-warning" disabled={saving}>{saving ? "Salvataggio…" : "Chiudi prezzo"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Modale "Ricalcola": parametri -> anteprima -> conferma -> esito ── */

function RecalcDialog({ model, tenants, tenantName, onClose }: {
  model: ModelPriceSummary;
  tenants: AdminTenant[];
  tenantName: (id: string | null) => string;
  onClose: () => void;
}) {
  // Se il prezzo corrente non ha un `valid_from` (voce YAML, non riga DB),
  // il default NON dev'essere "adesso": un'anteprima lanciata subito dopo
  // non troverebbe righe nell'intervallo, o il backend rifiuterebbe un
  // istante nel futuro immediato per clock skew. Si usa l'inizio della
  // giornata italiana odierna, sempre sicuro.
  const [dateFrom, setDateFrom] = useState(
    isoToLocalInput(model.current?.valid_from ?? dayToIso(todayYmd())),
  );
  const [dateTo, setDateTo] = useState(isoToLocalInput(null));
  const [tenantId, setTenantId] = useState("");
  const [report, setReport] = useState<RecalcReportView | null>(null);
  const [result, setResult] = useState<RecalcApplyView | null>(null);
  const [busy, setBusy] = useState<"" | "preview" | "apply">("");
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const [stale, setStale] = useState(false);

  const params = useMemo<RecalcRequest | string>(() => {
    if (!dateFrom || !dateTo) return "Servono inizio e fine dell'intervallo";
    const from = localToIso(dateFrom);
    const to = localToIso(dateTo);
    if (new Date(from).getTime() >= new Date(to).getTime()) return "La fine dell'intervallo deve essere dopo l'inizio";
    return { model_id: model.model_id, date_from: from, date_to: to, tenant_id: tenantId || null };
  }, [dateFrom, dateTo, tenantId, model.model_id]);

  async function runPreview() {
    setError("");
    setStale(false);
    setResult(null);
    if (typeof params === "string") { setError(params); return; }
    setBusy("preview");
    try {
      setReport(await adminRecalcPreview(params));
    } catch (err) {
      setReport(null);
      setError(errorMessage(err));
    } finally {
      setBusy("");
    }
  }

  async function runApply() {
    if (!report || typeof params === "string") return;
    setBusy("apply");
    setError("");
    try {
      const res = await adminRecalcApply({ ...params, fingerprint: report.fingerprint });
      setResult(res);
      setConfirming(false);
    } catch (err) {
      setConfirming(false);
      if (err instanceof AdminPricesApiError && err.status === 409) {
        // L'insieme delle righe e' cambiato sotto i piedi (altro apply, nuove
        // chiamate, prezzo nuovo): l'anteprima va rifatta, non forzata.
        setReport(null);
        setStale(true);
      } else {
        setError(errorMessage(err));
      }
    } finally {
      setBusy("");
    }
  }

  const done = result != null;
  const overlay = useOverlayGuard(!!busy, onClose);

  return (
    <>
      <div className="dialog-overlay" {...overlay}>
        <div className="dialog-card" role="dialog" aria-modal="true" aria-labelledby="recalc-title" onClick={(e) => e.stopPropagation()}>
          <div className="dialog-header">
            <h2 id="recalc-title">Ricalcola costi — {model.model_id}</h2>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={!!busy} aria-label="Chiudi">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          {!done && (
            <>
              <p className="text-sm text-muted mb-4">
                Riprezza le chiamate registrate nell'intervallo con il prezzo valido alla loro data.
                L'anteprima non cambia nulla; l'applicazione chiede conferma e scrive una riga di audit.
              </p>
              <div className="form-group">
                <label className="form-label" htmlFor="rc-from">Dal</label>
                <input id="rc-from" className="form-input" type="datetime-local" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setReport(null); }} />
                <small className="form-hint">
                  Predefinito: {model.current?.valid_from
                    ? `inizio validita' del prezzo corrente (${fmtDateTime(model.current.valid_from)})`
                    : "inizio della giornata odierna, fuso italiano"}.
                </small>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="rc-to">Al</label>
                <input id="rc-to" className="form-input" type="datetime-local" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setReport(null); }} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="rc-tenant">Cliente (opzionale)</label>
                <select id="rc-tenant" className="form-select" value={tenantId} onChange={(e) => { setTenantId(e.target.value); setReport(null); }}>
                  <option value="">Tutti i clienti</option>
                  {tenants.map((t) => <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>)}
                </select>
              </div>
            </>
          )}

          {stale && (
            <div className="alert alert-warning mb-4" role="alert" data-testid="recalc-stale">
              <span className="material-symbols-outlined">warning</span>
              L'anteprima non e' piu' valida, rilanciala.
            </div>
          )}
          {error && (
            <div className="alert alert-danger mb-4" role="alert" data-testid="recalc-error">
              <span className="material-symbols-outlined">error</span>
              {error}
            </div>
          )}

          {report && !done && <RecalcReportBody report={report} tenantName={tenantName} />}

          {done && result && (
            <div data-testid="recalc-result">
              <div className="alert alert-success mb-4" role="status">
                <span className="material-symbols-outlined">check_circle</span>
                Ricalcolo applicato: {fmtInt(result.changed)} righe cambiate
                {result.audit_id != null ? ` · audit ${result.audit_id}` : ""}.
              </div>
              <RecalcReportBody report={result.report} tenantName={tenantName} applied />
            </div>
          )}

          <div className="dialog-footer">
            {done ? (
              <button type="button" className="btn btn-primary" onClick={onClose}>Chiudi</button>
            ) : (
              <>
                <button type="button" className="btn btn-ghost" onClick={onClose} disabled={!!busy}>Annulla</button>
                <button type="button" className="btn btn-outline" onClick={runPreview} disabled={!!busy} data-testid="recalc-preview-btn">
                  {busy === "preview" ? "Anteprima…" : report ? "Rilancia anteprima" : "Anteprima"}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => setConfirming(true)}
                  disabled={!report || !!busy || report.would_change === 0}
                  title={report && report.would_change === 0 ? "Nessuna riga cambierebbe" : undefined}
                  data-testid="recalc-apply-btn"
                >
                  Applica
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {confirming && report && (
        <ConfirmModal
          title="Applicare il ricalcolo?"
          message={
            <>
              Verranno aggiornate <strong>{fmtInt(report.would_change)}</strong> righe di costo su {fmtInt(report.examined)} esaminate,
              per un delta totale di <strong>{fmtDelta(report.delta_total_usd)}</strong>. L'operazione e' registrata nell'audit e non si annulla da sola:
              per tornare indietro serve un altro ricalcolo.
            </>
          }
          confirmLabel="Applica ricalcolo"
          danger
          loading={busy === "apply"}
          onConfirm={runApply}
          onCancel={() => { if (busy !== "apply") setConfirming(false); }}
        />
      )}
    </>
  );
}

function RecalcReportBody({ report, tenantName, applied }: {
  report: RecalcReportView;
  tenantName: (id: string | null) => string;
  applied?: boolean;
}) {
  const notRecomputable = report.not_recomputable ?? [];
  const byTenant = report.delta_by_tenant ?? [];
  return (
    <div data-testid="recalc-report">
      <div className="kpi-grid mb-4">
        <div className="card kpi-tile">
          <span className="kpi-icon material-symbols-outlined">search</span>
          <div>
            <div className="kpi-value">{fmtInt(report.examined)}</div>
            <div className="kpi-label">Righe esaminate</div>
          </div>
        </div>
        <div className="card kpi-tile">
          <span className="kpi-icon material-symbols-outlined">edit</span>
          <div>
            <div className="kpi-value">{fmtInt(report.would_change)}</div>
            <div className="kpi-label">{applied ? "Righe cambiate" : "Righe che cambierebbero"}</div>
          </div>
        </div>
        <div className="card kpi-tile">
          <span className="kpi-icon material-symbols-outlined">attach_money</span>
          <div>
            <div className="kpi-value">{fmtDelta(report.delta_total_usd)}</div>
            <div className="kpi-label">Delta totale</div>
          </div>
        </div>
      </div>

      <h3 className="text-sm fw-semibold mb-2">Delta per cliente</h3>
      {byTenant.length === 0 ? (
        <p className="text-sm text-muted mb-4">Nessuna riga cambierebbe.</p>
      ) : (
        <div className="table-scroll mb-4">
          <table className="table" data-testid="recalc-by-tenant">
            <thead><tr><th>Cliente</th><th>Delta (€)</th></tr></thead>
            <tbody>
              {byTenant.map((r) => (
                <tr key={r.tenant_id ?? "__none__"}>
                  <td title={r.tenant_id ?? "Chiamate senza cliente nel contesto"}>{tenantName(r.tenant_id)}</td>
                  <td>{fmtDelta(r.delta_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="text-sm fw-semibold mb-2">Righe non ricalcolabili</h3>
      {notRecomputable.length === 0 ? (
        <p className="text-sm text-muted mb-4">Tutte le righe esaminate hanno quantita' e prezzo.</p>
      ) : (
        <div className="table-scroll mb-4">
          <table className="table" data-testid="recalc-not-recomputable">
            <thead><tr><th>Motivo</th><th>Righe</th></tr></thead>
            <tbody>
              {notRecomputable.map((r) => (
                <tr key={r.reason}>
                  <td title={r.reason}>{reasonLabel(r.reason)}</td>
                  <td>{fmtInt(r.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-muted">
        Intervallo {fmtDateTime(report.params?.date_from)} – {fmtDateTime(report.params?.date_to)}
        {report.params?.tenant_id ? ` · cliente ${tenantName(report.params.tenant_id)}` : " · tutti i clienti"}
        {" · "}impronta <code>{report.fingerprint ? report.fingerprint.slice(0, 12) : NO_DATA}</code>
      </p>
    </div>
  );
}
